"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Award, Heart, Landmark } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const values = [{
  title: "Integrity",
  description: "We operate with radical transparency and ethical precision, ensuring your digital infrastructure is built on a foundation of absolute trust.",
  icon: Award
}, {
  title: "Innovation",
  description: "Pushing the boundaries of AI and custom software to deliver futuristic solutions that keep your business at the cutting edge of technology.",
  icon: Heart
}, {
  title: "Security",
  description: "Our proactive cybersecurity protocols provide a multi-layered shield, protecting your most valuable data assets against evolving global threats.",
  icon: Landmark
}];
export default function OurCoreValues() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="265025" id="our-core-values" className="bg-background text-foreground py-24 border-y border-border/10 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(220,90%,60%,0.15)_0%,transparent_70%)] opacity-40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border/10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {values.map((value, i) => {
          const Icon = value.icon;
          return <div key={i} data-index={i} className="flex flex-col items-center text-center p-10 md:p-14 transition-all duration-500 hover:bg-secondary/5 group cursor-default">
                <div className="mb-8 p-5 rounded-lg bg-card/40 backdrop-blur-md border border-white/10 group-hover:border-primary/50 transition-all duration-500 shadow-xl">
                  <Icon className="w-8 h-8 text-primary group-hover:text-primary group-hover:scale-110 transition-all duration-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-default font-bold mb-4 tracking-tight text-foreground">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-base md:text-lg font-default">
                  {value.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}